import { useEffect } from 'react'
import { useModal } from '@ebay/nice-modal-react'
import { Icon } from '@iconify/react'
import clsx from 'clsx'
import { Menu } from 'antd'

import { Button, Dropdown, Spin } from '../../atoms'

import { useLocalStorage, useContentRefreshDomains } from '../../hooks'
import { useGlobalState } from '../../providers'

import Item from './Item'
import type { ContentRefreshDomain } from '@pepper/utils/src/types/domain'

import s from './DomainSelector.module.css'

type Props = {
  companyId: string
  isControlled: boolean
  disabled?: boolean
  showChevron?: boolean
  size?: 'small' | 'regular'
  value?: string
  shouldRedirectInNewTab?: boolean
  onChange?: (domainId: string, domain: ContentRefreshDomain) => void
}

const MAX_DOMAINS_ALLOWED = 5

export const DomainSelector = ({
  companyId,
  isControlled,
  disabled = false,
  showChevron = true,
  size = 'small',
  value,
  shouldRedirectInNewTab = false,
  onChange
}: Props) => {
  const { domainId, setDomainId } = useGlobalState()
  const [lsDomainId, setLsDomainId] = useLocalStorage('domain_id', '')
  const { show: showAddDomainModal } = useModal('add-domain')
  const {
    data: domains,
    isLoading,
    normalizedDomains,
    isGoogleConnected,
    refetch: refetchDomains
  } = useContentRefreshDomains(companyId)

  const handleChange = (_domainId: string, domain: ContentRefreshDomain) => {
    if (!isControlled) {
      setLsDomainId(_domainId)
      setDomainId(_domainId)
    }
    onChange?.(_domainId, domain)
  }

  const selectedDomainId = value ?? domainId ?? lsDomainId

  const selectedDomain = selectedDomainId
    ? normalizedDomains[selectedDomainId]
    : domains[0]

  useEffect(() => {
    if (!selectedDomainId && domains.length > 0) {
      handleChange(domains[0].id, domains[0])
    }
  }, [selectedDomainId])

  const menu = (
    <Menu className={s.menu}>
      {domains?.map(domain => {
        return (
          <Menu.Item
            key={domain.id}
            className={clsx(s.menuItem, {
              [s.isActive]: domainId === domain.id
            })}
            onClick={() => handleChange(domain.id, domain)}
          >
            <Item
              domain={domain}
              isGoogleConnected={isGoogleConnected}
              shouldRedirectInNewTab={shouldRedirectInNewTab}
            />
          </Menu.Item>
        )
      })}
      <Button
        type="ghost"
        block={true}
        className={s.addButton}
        icon={<Icon icon="lucide:plus" />}
        onClick={() => {
          showAddDomainModal({
            refetch: refetchDomains
          })
        }}
        disabled={domains.length >= MAX_DOMAINS_ALLOWED}
      >
        Add Domain
      </Button>
    </Menu>
  )

  return (
    <Dropdown overlay={menu} trigger={['click']} disabled={disabled}>
      <div
        className={clsx(
          'domain-selector',
          s.trigger,
          disabled && s.triggerDisabled,
          s[size]
        )}
        data-id="domain-added-domain-selector"
      >
        {isLoading ? (
          <Spin className={s.spinner} size="small" />
        ) : (
          <>
            {!!selectedDomain && (
              <Item
                domain={selectedDomain}
                isGoogleConnected={isGoogleConnected}
                showDomainNameTooltip={true}
                shouldRedirectInNewTab={shouldRedirectInNewTab}
              />
            )}
            {domains.length === 0 && (
              <span className={s.empty}>No domains found</span>
            )}
            {!selectedDomain && domains.length > 0 && (
              <span className={s.empty}>Select domain</span>
            )}
            {showChevron && (
              <Icon
                className={s.chevron}
                icon="lucide:chevron-down"
                width={15}
              />
            )}
          </>
        )}
      </div>
    </Dropdown>
  )
}
